// hooks/useGuestyPay.ts
import { useState, useEffect } from "react";
import { loadScript } from "@guestyorg/tokenization-js";

interface UseGuestyPayParams {
  step: number;
  integration_TYPE: string;
  Quote: any;
  providerId: string;
  listingID: string;
  formData: any;
  defaultCurrency: string;
  handleFetchReservation: (args: any) => Promise<void>;
}

interface UseGuestyPayReturn {
  renderGuestyPaymentForm: () => JSX.Element;
  handleGuestyPaymentSubmission: () => Promise<void>;
  errorMessage: string | null;
  isLoading: boolean;
}

export const useGuestyPay = ({
  step,
  integration_TYPE,
  Quote,
  providerId,
  listingID,
  formData,
  defaultCurrency,
  handleFetchReservation
}: UseGuestyPayParams): UseGuestyPayReturn => {
  const [guestyTokenization, setGuestyTokenization] = useState<any>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);


  // 1) Load the Guesty SDK only at step=3 and integration_TYPE="guesty"
  useEffect(() => {
    if (integration_TYPE !== "guesty" || step !== 3) return;

    const loadGuestySDK = async () => {
      try {
        // If you need sandbox environment, use environment: "sandbox"
        const sdk = await loadScript({ version: "v2" });
        setGuestyTokenization(sdk);
      } catch (error) {
        console.error("Failed to load Guesty Tokenization JS SDK:", error);
        setErrorMessage("Failed to load Guesty payment SDK. Please refresh the page.");
      }
    };

    loadGuestySDK();

    return () => {
      if (guestyTokenization) {
        try {
          guestyTokenization.destroy();
        } catch (err) {
          console.error("Error destroying Guesty tokenization instance:", err);
        }
      }
    };
  }, [integration_TYPE, step]);

  // 2) Render the Guesty iframe only if we have everything we need (step=3, etc.)
  useEffect(() => {
    const renderIframe = async () => {
      if (!guestyTokenization || !providerId || !Quote?.quoteId) return;

      const container = document.getElementById("guesty-tokenization-container");
      if (!container) {
        console.warn("No #guesty-tokenization-container found in DOM.");
        return;
      }

      try {
        console.log("Rendering Guesty Tokenization iframe...");
        await guestyTokenization.render({
          containerId: "guesty-tokenization-container",
          providerId,
        });
      } catch (error) {
        console.error("Failed to render Guesty Tokenization iframe:", error);
        setErrorMessage("Failed to load the payment form. Please refresh the page.");
      }
    };

    if (integration_TYPE === "guesty" && step === 3) {
      renderIframe();
    }
  }, [guestyTokenization, providerId, Quote, integration_TYPE, step]);

  // 3) Provide a function to return the container div
  const renderGuestyPaymentForm = () => (
    <div id="guesty-tokenization-container" className="my-4" style={{ minHeight: 200 }} />
  );

  // 4) Handle the Guesty payment submission
  const handleGuestyPaymentSubmission = async () => {
 
    if (!guestyTokenization) {
      setErrorMessage("Payment system is not ready. Please try again.");
      return;
    }
    if (!Quote?.quoteId) {
      setErrorMessage("Missing quote information. Please try again later.");
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);
   // console.log("Submitting payment...",listingID, Quote.quoteId, formData.guest, Quote.stayTotal, defaultCurrency);
    try {
      await guestyTokenization.validate();
      //console.log(defaultCurrency,listingID, Quote.quoteId, formData.guest, Quote.stayTotal);
      const payment = await guestyTokenization.submit({
        listingId: listingID,
        quoteId: Quote.quoteId,
        guest: formData.guest,
        amount: Quote.stayTotal,
        currency: defaultCurrency,
      });
      if (!payment || !payment._id) {
        throw new Error("Payment submission failed. Please try again.");
      }
console.log("Payment token received");
      // Once we have the token from Guesty, finalize the reservation
      await handleFetchReservation({
        quoteId: Quote.quoteId,
        ratePlanID: Quote.ratePlanId,
        ccToken: payment._id,
        guest: formData.guest,
        policy: formData.policy,
      });

      setErrorMessage(null);
    } catch (error: any) {
      console.error("Payment submission failed:", error);
      const message =
        error?.response?.data?.raw?.message ||
        "An error occurred during payment. Please try again.";
      setErrorMessage(message);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    renderGuestyPaymentForm,
    handleGuestyPaymentSubmission,
    errorMessage,
    isLoading,
  };
};